import React, { useState, useEffect } from "react";
import { Menu, X, Search } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Handle navigation + scroll to top
  const handleNavigate = (path) => {
    navigate(path);
    setOpenMenu(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ Sticky hide / show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setShowNavbar(false); // scrolling down
      } else {
        setShowNavbar(true); // scrolling up
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "My Space", path: "/myspace" },
    { name: "About", path: "/about" },
  ];

  const categories = [
    { name: "Cover Story", path: "/category/coverstory" },
    { name: "Interview", path: "/category/interview" },
    { name: "Story", path: "/category/story" },
    { name: "Poem", path: "/category/poem" },
    { name: "Review", path: "/category/review" },
    { name: "Zoom In", path: "/category/zoom in" },
    { name: "Article", path: "/category/article" },
    { name: "Standpoint", path: "/category/stand point" },
    { name: "Exclusive", path: "/category/exclusive" },
    { name: "Lookout", path: "/category/look out" },
    { name: "Festivities", path: "/category/festivities" },
    { name: "View In", path: "/category/view in" },
  ];

  const latestGuides = [
    {
      id: 5,
      imageUrl:
        "https://res.cloudinary.com/dkevvbzvn/image/upload/v1767643622/crvixkodysh3ztdfz7d4.png",
      title: "The Real KHEMIST STORY",
    },
    {
      id: 4,
      imageUrl:
        "https://res.cloudinary.com/dkevvbzvn/image/upload/v1767643633/rd4tzgashnfqtazb0awt.png",
      title: "Guide",
    },
    {
      id: 6,
      imageUrl:
        "https://res.cloudinary.com/dkevvbzvn/image/upload/v1767643643/id0oaycnoaqmgpqmsugm.png",
      title: "Guide",
    },
  ];

  return (
    <motion.div
      initial={{ y: 0 }}
      animate={{ y: showNavbar ? 0 : "-100%" }}
      transition={{ duration: 0.35, ease: "easeInOut" }}
      className="fixed top-0 left-0 w-full bg-white border-b shadow-sm z-50 font-blinker"
    >
      {/* TOP BAR */}
      <div className="max-w-7xl mx-auto px-4 py-3 relative flex items-center md:justify-between">

        {/* MOBILE LEFT */}
        <button
          onClick={() => setOpenMenu(!openMenu)}
          className="md:hidden p-2 hover:bg-gray-100 rounded-md"
        >
          {openMenu ? <X size={22} /> : <Menu size={22} />}
        </button>

        {/* MOBILE CENTER LOGO */}
        <h1
          className="md:hidden absolute left-1/2 -translate-x-1/2 text-2xl font-semibold cursor-pointer"
          onClick={() => handleNavigate("/")}
        >
          The Guide
        </h1>

        {/* MOBILE RIGHT */}
        <button
          onClick={() => handleNavigate("/search")}
          className="md:hidden ml-auto p-2 hover:bg-gray-100 rounded-md"
        >
          <Search size={20} />
        </button>

        {/* DESKTOP LEFT */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => setOpenMenu(!openMenu)}
            className="p-2 hover:bg-gray-100 rounded-md"
          >
            {openMenu ? <X size={22} /> : <Menu size={22} />}
          </button>

          <button
            onClick={() => handleNavigate("/search")}
            className="p-2 hover:bg-gray-100 rounded-md"
          >
            <Search size={20} />
          </button>
        </div>

        {/* DESKTOP LOGO */}
        <h1
          className="hidden md:block text-3xl font-semibold cursor-pointer"
          onClick={() => handleNavigate("/")}
        >
          The Guide
        </h1>

        {/* DESKTOP NAV */}
        <div className="hidden md:flex gap-6">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavigate(item.path)}
              className={`text-sm font-medium ${
                location.pathname === item.path
                  ? "text-red-600 border-b-2 border-red-600"
                  : "hover:text-gray-600"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      {/* MEGA MENU */}
      <AnimatePresence>
        {openMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-t shadow-xl max-h-[50vh] overflow-y-auto"
          >
            <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-10">

              {/* MOBILE NAV */}
              <div className="md:hidden flex gap-6 text-sm font-medium border-b pb-4">
                {navItems.map((item) => (
                  <span
                    key={item.name}
                    onClick={() => handleNavigate(item.path)}
                    className="cursor-pointer hover:text-red-600"
                  >
                    {item.name}
                  </span>
                ))}
              </div>

              {/* CATEGORIES */}
              <div className="md:col-span-2 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {categories.map((cat) => (
                  <span
                    key={cat.name}
                    onClick={() => handleNavigate(cat.path)}
                    className="cursor-pointer hover:text-red-600"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>

              {/* LATEST GUIDE */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <img
                    src={latestGuides[0].imageUrl}
                    alt={latestGuides[0].title}
                    className="w-full h-56 object-cover cursor-pointer"
                    onClick={() =>
                      handleNavigate(`/edition/${latestGuides[0].id}`)
                    }
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                  {latestGuides.slice(1).map((guide) => (
                    <img
                      key={guide.id}
                      src={guide.imageUrl}
                      alt={guide.title}
                      className="w-full h-28 object-cover cursor-pointer"
                      onClick={() =>
                        handleNavigate(`/edition/${guide.id}`)
                      }
                    />
                  ))}
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
